import { inject, observer } from 'mobx-react';
import React from 'react';
import MainSectionLayout from '../layout/MainSectionLayout';
import { Instance } from 'mobx-state-tree';
import { DutyExchangeStore } from '../../store/DutyExchangeStore';
import { ActionCode, Page, TLCode } from '../../common/enums';
import {
  ApplicationStore,
  AuthenticationStore,
  DutyStore,
  PharmacyDetailedStore,
  PharmacyStore,
  SecretariatStore,
  UiStore,
} from '../../store';
import { RouteComponentProps } from 'react-router-dom';
import styled from '@emotion/styled';
import { css } from 'emotion';
import { Pharmacy } from '../../store/models/Pharmacy';
import { DutyTypeStore } from '../../store/DutyTypeStore';
import { Card, Tab, Tabs } from '@blueprintjs/core';
import NewExchangeTab from './NewExchangeTab';
import ExchangeHistoryTab from './ExchangeHistoryTab';
import { PharmacyDetails } from '../../store/models/PharmacyDetails';
import { PharmacyNeighbor } from '../../store/models/PharmacyNeighbor';
import withTranslate, { WithTranslateProps } from '../common/WithTranslate';
import { DutyAssignment } from '../../store/models/DutyAssignment';
import { SystemStatusStore } from '../../store/SystemStatusStore';
import { SystemStatusSubSystem } from '../../common/enums/SystemStatusSubSystem';
import { SystemStatus } from '../../store/models/SystemStatus';
import { DutyAssignmentStore } from '../../store/DutyAssignmentStore';
import { ModalStore } from '../../store/ModalStore';
import { DutyExchangeItemStore } from '../../store/DutyExchangeItemStore';
import { DutyExchangeItem } from '../../store/models/DutyExchangeItem';

const Container = styled.div({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
});

const CardStyle = css({
  height: '100%',
  '& .bp3-tabs': {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  '& .bp3-tab-panel': {
    flex: 1,
  },
});

interface ExchangePageProps extends RouteComponentProps<{ pharmacyId: string }>, WithTranslateProps {
  AuthenticationStore?: Instance<typeof AuthenticationStore>;
  ApplicationStore?: Instance<typeof ApplicationStore>;
  PharmacyStore?: Instance<typeof PharmacyStore>;
  PharmacyDetailedStore?: Instance<typeof PharmacyDetailedStore>;
  DutyExchangeStore?: Instance<typeof DutyExchangeStore>;
  DutyExchangeItemStore?: Instance<typeof DutyExchangeItemStore>;
  DutyAssignmentStore?: Instance<typeof DutyAssignmentStore>;
  DutyTypeStore?: Instance<typeof DutyTypeStore>;
  DutyStore?: Instance<typeof DutyStore>;
  SystemStatusStore?: Instance<typeof SystemStatusStore>;
  ModalStore?: Instance<typeof ModalStore>;
  UiStore?: Instance<typeof UiStore>;
  SecretariatStore?: Instance<typeof SecretariatStore>;
}

interface ExchangePageState {
  isLoading: boolean;
  requestorDutiesIsLoading: boolean;
  neighborDutiesIsLoading: boolean;
  exchangeHistoryIsLoading: boolean;
  exchangeIsSaving: boolean;
  pharmacyExchangeRequesterSelected?: Instance<typeof PharmacyDetails>;
  pharmacyExchangeNeighborSelected?: Instance<typeof PharmacyDetails>;
  withAgreement?: boolean;
  withoutSubstitution?: boolean;
  comment?: string;
  pharmacyNeighboringList?: Array<Instance<typeof PharmacyDetails>>;
  duties?: Array<Instance<typeof DutyAssignment>>;
  neighborDuties?: Array<Instance<typeof DutyAssignment>>;
  requesterDutiesSelected: number[];
  neighborDutiesSelected: number[];
  pharmacyUser: boolean;
  pharmacyId?: number;
  systemStatus?: Array<Instance<typeof SystemStatus>>;
  exchangeItemHistory: Array<Instance<typeof DutyExchangeItem>>;
  onlyFutureDutyExchange?: boolean;
}

@inject('ModalStore')
@inject('AuthenticationStore')
@inject('ApplicationStore')
@inject('PharmacyStore')
@inject('PharmacyDetailedStore')
@inject('DutyExchangeStore')
@inject('DutyExchangeItemStore')
@inject('DutyAssignmentStore')
@inject('DutyTypeStore')
@inject('DutyStore')
@inject('SecretariatStore')
@inject('SystemStatusStore')
@inject('UiStore')
@observer
class ExchangePage extends React.Component<ExchangePageProps, ExchangePageState> {
  state = {
    isLoading: false,
    requestorDutiesIsLoading: false,
    neighborDutiesIsLoading: false,
    exchangeHistoryIsLoading: false,
    exchangeIsSaving: false,
    duties: [],
    neighborDuties: [],
    requesterDutiesSelected: [],
    neighborDutiesSelected: [],
    pharmacyUser: false,
    withAgreement: false,
    withoutSubstitution: false,
    comment: '',
    exchangeItemHistory: [],
    onlyFutureDutyExchange: true,
  } as ExchangePageState;

  _isMounted = false;

  async componentDidMount() {
    this._isMounted = true;
    this._isMounted && this.setState({ isLoading: true });
    const { PharmacyStore, AuthenticationStore, ApplicationStore, translate } = this.props;
    //await PharmacyStore!.loadList();
    await PharmacyStore!.loadListSafely(true, true);
    const userRoles = AuthenticationStore!.userActionCodes;

    const criteria: any = {
      subSystem: 'lastPublishedDuty',
    };
    const systemStatus = await this.props.SystemStatusStore!.search(criteria);

    // @ts-ignore
    this._isMounted && this.setState({ systemStatus });

    if (userRoles.includes(ActionCode.PHARMACY_USER_CODE)) {
      if (AuthenticationStore!.pharmacyId === null) {
        ApplicationStore!.navigate(`${Page.Error}/${translate(TLCode.ERROR_NoPharmacyRelatedToThisUser)}`);
      }
      this.onExchangeRequesterSelected(PharmacyStore!.getById(AuthenticationStore!.pharmacyId!)!);
      this._isMounted &&
        this.setState({
          pharmacyUser: true,
        });

      const criteriaBP: any = {
        pharmacyId: AuthenticationStore!.pharmacyId,
        subSystem: SystemStatusSubSystem.EXCHANGE,
      };
      const systemStatus = await this.props.SystemStatusStore!.search(criteriaBP);

      // @ts-ignore
      this._isMounted && this.setState({ systemStatus });
    }

    if (this.props.UiStore!.pharmaciesSecretariatIdFilter === 0)
      this.props.UiStore!.setPharmaciesSecretariatIdFilter(this.getSelectedSecretariatId());

    this._isMounted && this.setState({ isLoading: false });
  }

  componentWillUnmount() {
    this.props.DutyExchangeItemStore!.clearData();
    this._isMounted = false;
  }

  async ensureHistoryLoaded() {
    if (this.state.exchangeItemHistory.length === 0 && !this.state.exchangeHistoryIsLoading) {
      this.loadHistory(this.state.onlyFutureDutyExchange);
    }
  }

  switchWithPastDates = async () => {
    const currentValueOnlyUndownloadedValues = this.state.onlyFutureDutyExchange;
    this.loadHistory(!currentValueOnlyUndownloadedValues);
    this._isMounted && this.setState({ onlyFutureDutyExchange: !currentValueOnlyUndownloadedValues });
  };

  private async loadHistory(currentValueOnlyUndownloadedValues: boolean | undefined) {
    const criteria = this.state.pharmacyUser
      ? {
          pharmacyId: this.props.AuthenticationStore!.pharmacyId,
          onlyFutureDutyExchange: currentValueOnlyUndownloadedValues,
        }
      : !(this.props.AuthenticationStore!.isAdmin || this.props.AuthenticationStore!.isSuperAdmin) &&
        this.props.AuthenticationStore!.isSecretariat
      ? { onlyFutureDutyExchange: currentValueOnlyUndownloadedValues, secretariatId: this.getSelectedSecretariatId() }
      : { onlyFutureDutyExchange: currentValueOnlyUndownloadedValues };
    this._isMounted && this.setState({ exchangeHistoryIsLoading: true });
    const responses = await Promise.all([
      this.props.DutyExchangeItemStore!.search(criteria as any),
      this.props.DutyStore!.searchForDutyExchangeItem(criteria as any),
    ]);
    //@ts-ignore
    this._isMounted && this.setState({ exchangeItemHistory: responses[0] });
    this._isMounted && this.setState({ exchangeHistoryIsLoading: false });
  }

  onExchangeRequesterSelected = async (value: Instance<typeof Pharmacy>) => {
    this._isMounted && this.setState({ requestorDutiesIsLoading: true });
    const { DutyExchangeStore, PharmacyDetailedStore } = this.props;
    await Promise.all([
      DutyExchangeStore!.loadPharmacyDuties(value!.pharmacyId),
      PharmacyDetailedStore!.loadElement(value.pharmacyId)!,
      PharmacyDetailedStore!.loadNeighboringPharmaciesForExchanges(
        value.pharmacyId,
        this.props.AuthenticationStore!.isPharmacy,
      ),
    ]).then((response) => {
      const duties = response[0] as Array<Instance<typeof DutyAssignment>>;
      const pharmacy: any = response[1];
      const neighboringList = response[2];

      this._isMounted &&
        this.setState({
          pharmacyExchangeRequesterSelected: pharmacy,
          // @ts-ignore
          pharmacyNeighboringList: neighboringList,
          duties,
          requestorDutiesIsLoading: false,
        });
    });
  };

  onExchangeNeighborSelected = async (value) => {
    this._isMounted && this.setState({ neighborDutiesIsLoading: true });
    const { DutyExchangeStore, PharmacyDetailedStore } = this.props;
    const neighborDuties = (await DutyExchangeStore!.loadPharmacyDuties(value!.pharmacyId)) as Array<
      Instance<typeof DutyAssignment>
    >;
    const pharmacy: any = await PharmacyDetailedStore!.loadElement(value.pharmacyId)!;
    this._isMounted &&
      this.setState({
        pharmacyExchangeNeighborSelected: pharmacy,
        requesterDutiesSelected: [],
        neighborDutiesSelected: [],
        neighborDuties,
        neighborDutiesIsLoading: false,
      });
  };

  isInConflict = async (dutyAssignmentId, pharmacyId) => {
    return await this.props.DutyExchangeStore!.isInConflict(dutyAssignmentId, pharmacyId);
  };

  hasAlreadyDutyOn = async (dutyAssignmentId, pharmacyId) => {
    console.log('hasAlreadyDutyOn ' + dutyAssignmentId + ' with pharmacyId ' + pharmacyId);
    await this.props.DutyAssignmentStore!.searchAssignment({ dutyAssignmentId });
    const dutyType =
      this.props.DutyAssignmentStore!.getById(dutyAssignmentId)!.duty!.dutyType !== undefined
        ? this.props.DutyAssignmentStore!.getById(dutyAssignmentId)!.duty!.dutyType!.type
        : undefined;
    const dutyDate = this.props.DutyAssignmentStore!.getById(dutyAssignmentId)!.duty!.dutyDate.date;
    const result: any = await this.props.DutyAssignmentStore!.searchAssignment({
      pharmacyId,
      startDate: dutyDate,
      endDate: dutyDate,
      type: dutyType,
    });
    console.log('hasAlreadyDutyOn result.length > 0 : ' + (result.length > 0));
    return result.length > 0;
  };

  onSave = async () => {
    this._isMounted && this.setState({ exchangeIsSaving: true });
    const { DutyExchangeStore } = this.props;
    const { requesterDutiesSelected, neighborDutiesSelected } = this.state;
    const dutyExchange = {
      requestorId: this.state.pharmacyExchangeRequesterSelected!.pharmacyId,
      recipientId: this.state.pharmacyExchangeNeighborSelected!.pharmacyId,
      withAgreement: this.state.withAgreement,
      withSubstitution: this.state.withoutSubstitution!,
      comment: this.state.comment!,
      createdOn: Date.now.toString(),
      createdBy: '',
      lastModifiedOn: Date.now.toString(),
      lastModifiedBy: '',
    };
    const res = await DutyExchangeStore!.saveDutyExchange(
      dutyExchange,
      requesterDutiesSelected,
      neighborDutiesSelected,
    );
    const dutiesRequester = (await DutyExchangeStore!.loadPharmacyDuties(
      this.state.pharmacyExchangeRequesterSelected!.pharmacyId,
    )) as Array<Instance<typeof DutyAssignment>>;
    const dutiesNeighbor = (await DutyExchangeStore!.loadPharmacyDuties(
      this.state.pharmacyExchangeNeighborSelected!.pharmacyId,
    )) as Array<Instance<typeof DutyAssignment>>;
    this._isMounted &&
      this.setState({
        duties: dutiesRequester,
        neighborDuties: dutiesNeighbor,
        requesterDutiesSelected: [],
        neighborDutiesSelected: [],
        withAgreement: false,
        withoutSubstitution: false,
        comment: '',
        exchangeIsSaving: false,
      });
    return res;
  };

  onWithoutSubstitutionChange = async () => {
    if (!this.state.withoutSubstitution && this.state.neighborDutiesSelected.length > 0) {
      const confirm = await this.props.ModalStore!.confirm.show(
        this.props.translate(TLCode.GENERAL_ConfirmationDemand),
        this.props.translate(TLCode.EXCHANGE_NeighborDutiesReinitialisation),
      );
      if (confirm) {
        this._isMounted &&
          this.setState({ neighborDutiesSelected: [], withoutSubstitution: !this.state.withoutSubstitution });
      }
    } else {
      this._isMounted && this.setState({ withoutSubstitution: !this.state.withoutSubstitution });
    }
  };

  onWithAgreementChange = () => {
    this.setState({ withAgreement: !this.state.withAgreement });
  };

  onCommentChange = (value) => {
    this.setState({ comment: value });
  };

  private getSelectedSecretariatId(): number | undefined {
    return this.props.AuthenticationStore!.isPharmacy &&
      this.props.SecretariatStore!.dataList.find(
        (s) =>
          s.secretariatId ===
          this.props.PharmacyStore!.dataList.find((p) => p.pharmacyId === this.props.AuthenticationStore!.pharmacyId)!
            .secretariatId,
      ) !== undefined
      ? this.props.PharmacyStore!.dataList.find((p) => p.pharmacyId === this.props.AuthenticationStore!.pharmacyId)!
          .secretariatId
      : this.props.AuthenticationStore!.isSecretariat && this.props.AuthenticationStore!.secretariatId
      ? this.props.AuthenticationStore!.secretariatId
      : this.props.SecretariatStore!.selectedSecretariatId;
  }

  render() {
    const {
      pharmacyExchangeRequesterSelected,
      pharmacyExchangeNeighborSelected,
      duties,
      neighborDuties,
      pharmacyNeighboringList,
      pharmacyUser,
      systemStatus,
      requesterDutiesSelected,
      neighborDutiesSelected,
      withAgreement,
      comment,
      withoutSubstitution,
      exchangeItemHistory,
      requestorDutiesIsLoading,
      neighborDutiesIsLoading,
      exchangeIsSaving,
    } = this.state;

    const { PharmacyStore, translate } = this.props;

    const secretariatIdFilter = this.props.UiStore!.pharmaciesSecretariatIdFilter;
    const pharmacyList = PharmacyStore!.dataList.filter(
      (p) =>
        p.level > 0 &&
        (secretariatIdFilter === undefined || secretariatIdFilter === 0 || p.secretariatId === secretariatIdFilter),
    );

    const pharmacyConnected =
      this.props.AuthenticationStore!.pharmacyId !== null
        ? PharmacyStore!.getById(this.props.AuthenticationStore!.pharmacyId!)
        : undefined;

    const finalNeighborsList =
      pharmacyNeighboringList !== undefined
        ? pharmacyNeighboringList.filter(
            (p) => !pharmacyUser || pharmacyConnected === undefined || pharmacyConnected.groupId === p.groupId,
          )
        : undefined;

    return (
      <MainSectionLayout isLoading={this.state.isLoading} title={TLCode.EXCHANGE_Title}>
        <Container>
          <Card className={CardStyle.toString()}>
            <Tabs
              id="main"
              defaultSelectedTabId="newExchange"
              renderActiveTabPanelOnly={true}
              onChange={async (newTabId) => {
                if (newTabId === 'history') {
                  await this.ensureHistoryLoaded();
                }
              }}
            >
              <Tab
                id="newExchange"
                title={translate(TLCode.EXCHANGE_NewExchangeTabTitle)}
                panel={
                  <NewExchangeTab
                    onExchangeRequesterSelected={this.onExchangeRequesterSelected}
                    onExchangeNeighborSelected={this.onExchangeNeighborSelected}
                    pharmacyList={pharmacyList}
                    pharmacyNeighboringList={finalNeighborsList}
                    pharmacyRequesterSelected={pharmacyExchangeRequesterSelected}
                    pharmacyNeighborSelected={pharmacyExchangeNeighborSelected}
                    duties={duties}
                    neighborDuties={neighborDuties}
                    onRequesterDutiesSelected={(ids: number[]) => this.setState({ requesterDutiesSelected: ids })}
                    onNeighborDutiesSelected={(ids: number[]) => this.setState({ neighborDutiesSelected: ids })}
                    onSave={() => this.onSave()}
                    getPharmacyById={PharmacyStore!.getById}
                    pharmacyUser={pharmacyUser}
                    systemStatus={systemStatus}
                    requesterDutiesSelected={requesterDutiesSelected}
                    neighborDutiesSelected={neighborDutiesSelected}
                    withAgreement={withAgreement}
                    comment={comment}
                    withoutSubstitution={withoutSubstitution}
                    onWithoutSubstitutionChange={this.onWithoutSubstitutionChange}
                    onWithAgreementChange={this.onWithAgreementChange}
                    onCommentChange={this.onCommentChange}
                    requestorDutiesIsLoading={requestorDutiesIsLoading}
                    neighborDutiesIsLoading={neighborDutiesIsLoading}
                    exchangeIsSaving={exchangeIsSaving}
                    isInConflict={this.isInConflict}
                    hasAlreadyDutyOn={this.hasAlreadyDutyOn}
                  />
                }
              />
              <Tab
                id="history"
                title={translate(TLCode.EXCHANGE_HistoryTabTitle)}
                panel={
                  <ExchangeHistoryTab
                    dutyExchangeList={exchangeItemHistory}
                    exchangeHistoryIsLoading={this.state.exchangeHistoryIsLoading}
                    OnCheckedPastDates={this.switchWithPastDates}
                    isCheckBoxPastDateSelected={!this.state.onlyFutureDutyExchange}
                  />
                }
              />
            </Tabs>
          </Card>
        </Container>
      </MainSectionLayout>
    );
  }
}

export default withTranslate(ExchangePage);
